import { MaterialReactTable } from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import {
  Container,
  Box,
  Typography,
  Button,
  createTheme,
  ThemeProvider,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { UrlApi } from "../../../services/apirest";
import { useToast } from "../../../Hooks/UseToast";
import { useNavigate } from "react-router-dom";
import Navbar from "../../Navbar";
import moment from "moment";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const DataTableRejected = () => {
  const navigate = useNavigate();
  const { Toast, showToast } = useToast();
  const [rechazados, setRechazados] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());

  const currentYear = new Date().getFullYear();
  const yearOptions = [
    currentYear - 2,
    currentYear - 1,
    currentYear,
    currentYear + 1,
    currentYear + 2,
  ];

  const initialState = {
    density: "compact",
    showGlobalFilter: true,
    pagination: { pageIndex: 0, pageSize: 10 },
  };

  const muiSearchTextFieldProps = {
    placeholder: "Buscar...",
    sx: { minWidth: "18rem", backgroundColor: "#ffffff" },
    variant: "outlined",
    color: "primary",
    size: "small",
  };

  const tableHeadCellProps = {
    align: "left",
    sx: {
      fontSize: 16,
      color: "white",
      fontWeight: "normal",
      backgroundColor: "#2471A3",
      borderColor: "black",
    },
  };

  const formatDate = (fecha) => {
    if (!fecha) return "";
    return moment(fecha).format("DD/MM/YYYY");
  };

  const columns = useMemo(
    () => [
      { header: "ID", accessorKey: "rejectedId", size: 0 },
      { header: "Legajo", accessorKey: "legajo", size: 80 },
      { header: "Empleado", accessorKey: "nombreApellido", size: 120 },
      { header: "Sucursal", accessorKey: "branchId", size: 80 },
      {
        header: "Inicio Vacaciones",
        accessorFn: (row) => formatDate(row.fechaInicio),
        size: 80,
      },
      { header: "Días devueltos", accessorKey: "dias", size: 40 },
      {
        header: "Motivo del rechazo",
        accessorKey: "razon",
        size: 200,
      },
      {
        header: "Rechazado por",
        accessorKey: "rejectedBy",
        size: 80,
      },
      {
        header: "Fecha de rechazo",
        accessorFn: (row) => formatDate(row.rejectedAt),
        size: 80,
      },
    ],
    []
  );

  const globalTheme = useTheme();
  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: globalTheme.palette.mode,
          primary: globalTheme.palette.primary,
          info: { main: "rgb(255,122,0)" },
          background: {
            default:
              globalTheme.palette.mode === "light"
                ? "rgb(234, 239, 241)"
                : "#000",
          },
        },
        typography: { button: { textTransform: "none", fontSize: "1rem" } },
        components: {
          MuiTooltip: {
            styleOverrides: { tooltip: { fontSize: "0.8rem" } },
          },
        },
      }),
    [globalTheme]
  );

  const getDataRechazados = async () => {
    try {
      const url = `${UrlApi}/vacations/rejecteds/${year}`;
      const { data } = await axios.get(url);
      const list = data.data || data;
      setRechazados(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("[DataTableRejected] Error al traer rechazados:", err);
      showToast(
        `No se encontraron solicitudes rechazadas en el ${year}`,
        "error"
      );
    }
  };

  useEffect(() => {
    getDataRechazados();
  }, [year]);

  return (
    <>
      <Navbar />
      <Container maxWidth={false} sx={{ width: "100%" }} mt={1}>
        {rechazados && (
          <Box mt={2} mb={4}>
            <ThemeProvider theme={tableTheme}>
              <MaterialReactTable
                enableToolbarInternalActions
                autoResetPageIndex={false}
                enableGrouping
                layoutMode="grid"
                muiTableHeadCellProps={{
                  align: "left",
                  sx: {
                    fontSize: 15,
                    color: "white",
                    fontWeight: 600,
                    backgroundColor: "#1565C0",
                    borderBottom: "2px solid #0D47A1",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  },
                }}
                muiTableBodyCellProps={{
                  align: "left",
                  sx: {
                    padding: "10px 12px",
                    fontSize: "0.9rem",
                    borderColor: "#e0e0e0",
                    backgroundColor: "#ffffff",
                    transition: "background-color 0.2s ease",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  },
                }}
                muiTablePaperProps={{
                  elevation: 3,
                  sx: {
                    borderRadius: "10px",
                    overflow: "hidden",
                  },
                }}
                enableColumnActions
                enableColumnFilters={false}
                enableHiding={false}
                enableDensityToggle={false}
                enableColumnDragging={false}
                enableStickyFooter
                localization={MRT_Localization_ES}
                initialState={initialState}
                muiSearchTextFieldProps={{
                  placeholder: "Buscar...",
                  sx: {
                    minWidth: "18rem",
                    backgroundColor: "#fafafa",
                    borderRadius: "8px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#bdbdbd",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#1976D2",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#1976D2",
                      borderWidth: "2px",
                    },
                  },
                  variant: "outlined",
                  color: "primary",
                  size: "small",
                }}
                columns={columns}
                data={rechazados}
                positionActionsColumn="last"
                renderTopToolbarCustomActions={() => (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      paddingY: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          color: "#0D47A1",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Vacaciones rechazadas
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel id="year-select-label">Año</InputLabel>
                        <Select
                          labelId="year-select-label"
                          value={year}
                          label="Año"
                          onChange={(e) => setYear(e.target.value)}
                        >
                          {yearOptions.map((y) => (
                            <MenuItem key={y} value={y}>
                              {y}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant="contained"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                          backgroundColor: "#1565C0",
                          borderRadius: "8px",
                          paddingX: 3,
                          fontWeight: 600,
                          "&:hover": {
                            backgroundColor: "#0D47A1",
                          },
                        }}
                        onClick={() => navigate("/vacaciones")}
                      >
                        Volver
                      </Button>
                    </Box>
                  </Box>
                )}
              />
            </ThemeProvider>
          </Box>
        )}
      </Container>
      {Toast}
    </>
  );
};

export default DataTableRejected;
